import React, {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
} from 'react';
import { getShipViewGroups } from '../../services/VoyageRepository';
import { useReportingFilters } from './ReportingFiltersContext';
import type { ApiRecord, RequestParams } from './types';

export interface ReportingShipViewDataContextValue {
  shipViewGroups: ApiRecord[];
  shipViewGroupsBase: ApiRecord[];
  shipViewCurrentPage: number;
  shipViewTotalPages: number;
  fetchShipViewPage: (page: number) => Promise<void>;
}

const ReportingShipViewDataContext =
  createContext<ReportingShipViewDataContextValue | null>(null);

export const ReportingShipViewDataProvider = ({
  children,
}: PropsWithChildren) => {
  const { buildParams, activeScenario, showError } = useReportingFilters();

  const [shipViewGroups, setShipViewGroups] = useState<ApiRecord[]>([]);
  const [shipViewGroupsBase, setShipViewGroupsBase] = useState<ApiRecord[]>([]);
  const [shipViewCurrentPage, setShipViewCurrentPage] = useState(1);
  const [shipViewTotalPages, setShipViewTotalPages] = useState(0);

  const fetchShipViewPage = useCallback(
    async (page: number) => {
      try {
        setShipViewCurrentPage(page);
        const paramsObj = Object.fromEntries(buildParams().entries());
        const params: RequestParams = {
          ...paramsObj,
          page: page - 1,
          size: 10,
        };

        const scenarioFetch = getShipViewGroups(params);

        // When scenario is active, also fetch base (without scenarioId) for comparison
        let baseFetch: Promise<ApiRecord> | undefined;
        if (activeScenario) {
          const baseParams = { ...params };
          delete baseParams.scenarioId;
          baseFetch = getShipViewGroups(baseParams).catch(() => ({
            content: [],
            totalPages: 0,
          }));
        }

        const response = await scenarioFetch;
        const { content, totalPages } = response;
        setShipViewGroups(content || []);
        setShipViewTotalPages(totalPages || 0);

        if (baseFetch) {
          const baseResponse = await baseFetch;
          setShipViewGroupsBase(baseResponse.content || []);
        } else {
          setShipViewGroupsBase([]);
        }
      } catch (error) {
        showError(error, 'fetchShipViewPage');
        setShipViewGroups([]);
        setShipViewGroupsBase([]);
        setShipViewTotalPages(0);
      }
    },
    [buildParams, activeScenario, showError]
  );

  return (
    <ReportingShipViewDataContext.Provider
      value={{
        shipViewGroups,
        shipViewGroupsBase,
        shipViewCurrentPage,
        shipViewTotalPages,
        fetchShipViewPage,
      }}
    >
      {children}
    </ReportingShipViewDataContext.Provider>
  );
};

export const useReportingShipViewData =
  (): ReportingShipViewDataContextValue => {
    const ctx = useContext(ReportingShipViewDataContext);
    if (ctx === null) {
      throw new Error(
        'useReportingShipViewData must be used within a <ReportingShipViewDataProvider>'
      );
    }
    return ctx;
  };
